import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Tabs as T } from 'radix-ui';
import { cn } from '@/lib/cn';
import { useScrollFade } from './useScrollFade';

export const Tabs = T.Root;
export const TabsContent = ({ className, ...rest }: ComponentPropsWithoutRef<typeof T.Content>) => (
  <T.Content
    className={cn('pt-5 focus-visible:outline-none data-[state=active]:animate-fade', className)}
    {...rest}
  />
);

/**
 * `underline` (default): tabs on a hairline, the active tab gets a brand-blue rule.
 * `segmented`: compact pill group for view switches (e.g. Day / Week / Month).
 */
export function TabsList({
  className,
  variant = 'underline',
  ...rest
}: ComponentPropsWithoutRef<typeof T.List> & { variant?: 'underline' | 'segmented' }) {
  // Overflowing tab bars fade at the hidden edge, snap, and bring the active tab into view.
  const fade = useScrollFade<HTMLDivElement>({ revealActive: true });
  return (
    <T.List
      ref={fade}
      data-variant={variant}
      className={cn(
        'group/tabs scroll-fade',
        variant === 'underline'
          ? // pl-[3px]: room for the overhanging tonos of a first «Όλα» tab, which the scroll box would clip.
            // scroll-pl matches it, or start-snapping scrolls the first tab 3px under the edge.
            'flex gap-6 overflow-x-auto border-b border-hairline pl-[3px] scroll-pl-[3px]'
          : 'inline-flex max-w-full gap-0.5 overflow-x-auto rounded-sm bg-sunken p-[3px] scroll-px-[3px]',
        className,
      )}
      {...rest}
    />
  );
}

export function TabsTrigger({
  className,
  count,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof T.Trigger> & { count?: ReactNode }) {
  return (
    <T.Trigger
      className={cn(
        'group relative inline-flex items-center gap-2 whitespace-nowrap text-[0.875rem] font-semibold text-ink-muted transition-colors',
        'hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:opacity-50',
        // underline
        'group-data-[variant=underline]/tabs:-mb-px group-data-[variant=underline]/tabs:border-b-2 group-data-[variant=underline]/tabs:border-transparent group-data-[variant=underline]/tabs:pb-2.5 group-data-[variant=underline]/tabs:pt-1',
        'group-data-[variant=underline]/tabs:data-[state=active]:border-primary group-data-[variant=underline]/tabs:data-[state=active]:text-ink',
        // segmented
        'group-data-[variant=segmented]/tabs:h-7 group-data-[variant=segmented]/tabs:rounded-[4px] group-data-[variant=segmented]/tabs:px-3 group-data-[variant=segmented]/tabs:text-sm',
        'group-data-[variant=segmented]/tabs:data-[state=active]:bg-surface group-data-[variant=segmented]/tabs:data-[state=active]:text-ink group-data-[variant=segmented]/tabs:data-[state=active]:shadow-xs',
        className,
      )}
      {...rest}
    >
      {children}
      {count !== undefined && (
        <span className="num rounded-full bg-sunken px-1.5 text-2xs font-semibold text-ink-muted group-data-[state=active]:bg-primary group-data-[state=active]:text-on-primary">
          {count}
        </span>
      )}
    </T.Trigger>
  );
}
